/**
 * Roteador do mock: recebe (método, caminho, corpo) e devolve { status, data }.
 * Cobre 100% dos endpoints que o dash-web consome (BFF: auth, metabase, uploads;
 * legacy-api via /bff/api/*). Tudo em memória, com mutações persistindo na sessão.
 */
import { firstPassbookId, passbookIdsOf } from '@/shared/lib/passbook-access';
import type { Lead, Passbook, Product } from '@/shared/types';
import type { IndividualMessage } from '@/shared/types/individual-message';
import { previewOperatorBill } from '@/shared/lib/loyalty-engine';
import { uniqueIdsMatch } from '@/shared/lib/unique-id-lookup';
import { buildAutomationCampaigns, createDb, userFromEmail } from './data';
import { handleNps } from './nps';
import { mockDispatchRuns } from './dispatch-runs';
import { handleAiAdmin, handleAiModels } from './ai-admin';
import { handleMgmAdmin, handleMgmPublic } from './mgm';
import { handleParticipantPublic, participantInviteOverrides } from './participant';
import { getObservabilityRetention, queryObservability, saveObservabilityRetention } from './observability';
import { handleBillingMock, handleBillingQuota } from './billing';
import { handleSegmentationMock } from './segmentations';
import { handleMarketingAdminMock, handleMarketingMock } from './marketing';

const db = createDb();

/** Dedup por externalId — espelha Log.findOne({ id_passbook, externalId }) (user.js ~9219). */
const seenExternalIds = new Set<string>();
/** externalIds de resgate por passbook — regex resgateNaoPontua (user.js ~5151). */
const redemptionExternalIdsByPassbook = new Map<string, Set<string>>();
/** Cadastros do form público de invite (`idPassbook:uniqueId` → dados) — demonstra o fluxo de "cadastro duplicado". */
const inviteRegistrations = new Map<string, { email: string; serialNumber: string }>();
/** Flag do Varejo Online por loja — o token não mora aqui. */
const varejonlineByPassbook = new Map<string, { active: boolean }>();
/** Campanhas automatizadas excluídas nesta sessão. O GET regenera a lista e precisa respeitar isso. */
const removedAutomationIds = new Set<string>();

/** As 27 UFs (mock do vipcell/estado-cidade) — algumas capitais/cidades grandes por estado, não exaustivo. */
const MOCK_CITIES_BY_STATE: Record<string, string[]> = {
  AC: ['Rio Branco', 'Cruzeiro do Sul'],
  AL: ['Maceió', 'Arapiraca'],
  AP: ['Macapá', 'Santana'],
  AM: ['Manaus', 'Parintins'],
  BA: ['Salvador', 'Feira de Santana', 'Vitória da Conquista'],
  CE: ['Fortaleza', 'Juazeiro do Norte'],
  DF: ['Brasília'],
  ES: ['Vitória', 'Vila Velha', 'Serra'],
  GO: ['Goiânia', 'Aparecida de Goiânia'],
  MA: ['São Luís', 'Imperatriz'],
  MT: ['Cuiabá', 'Várzea Grande'],
  MS: ['Campo Grande', 'Dourados'],
  MG: ['Belo Horizonte', 'Uberlândia', 'Juiz de Fora'],
  PA: ['Belém', 'Ananindeua'],
  PB: ['João Pessoa', 'Campina Grande'],
  PR: ['Curitiba', 'Londrina', 'Maringá'],
  PE: ['Recife', 'Jaboatão dos Guararapes'],
  PI: ['Teresina', 'Parnaíba'],
  RJ: ['Rio de Janeiro', 'Niterói', 'Duque de Caxias'],
  RN: ['Natal', 'Mossoró'],
  RS: ['Porto Alegre', 'Caxias do Sul', 'Pelotas'],
  RO: ['Porto Velho', 'Ji-Paraná'],
  RR: ['Boa Vista'],
  SC: ['Florianópolis', 'Joinville', 'Blumenau'],
  SP: ['São Paulo', 'Campinas', 'Santos', 'Ribeirão Preto'],
  SE: ['Aracaju', 'Nossa Senhora do Socorro'],
  TO: ['Palmas', 'Araguaína'],
};

const individualMessagesDb: Record<string, IndividualMessage[]> = {};

const MOCK_MESSAGE_SAMPLES = [
  'Seu cartão foi atualizado! Você ganhou pontos na última compra.',
  'Promoção Tea Shop: 2x pontos em todas as lojas neste fim de semana.',
  'Lembrete: seus pontos expiram em 30 dias. Aproveite para resgatar prêmios.',
  'Novidade no cardápio! Experimente o Watermelon Paradise e ganhe pontos extras.',
  'Parabéns! Você está a poucos pontos de resgatar seu brinde favorito.',
  'Obrigado por visitar a Tea Shop. Avalie sua experiência e ganhe benefícios.',
];

function seedIndividualMessages() {
  for (const [passbookId, leads] of Object.entries(db.leads)) {
    leads.forEach((lead, index) => {
      const uniqueId = lead.profile?.uniqueId;
      if (!uniqueId) return;

      const key = messagesKey(passbookId, uniqueId);
      const count = 2 + (index % 4);
      const messages: IndividualMessage[] = [];

      for (let j = 0; j < count; j++) {
        const date = new Date();
        date.setDate(date.getDate() - j * 5 - (index % 3));
        date.setHours(10 + j, 30 + index, 0, 0);
        messages.push({
          _id: `mock-msg-${passbookId}-${index}-${j}`,
          id_passbook: passbookId,
          uniqueId,
          description: MOCK_MESSAGE_SAMPLES[(index + j) % MOCK_MESSAGE_SAMPLES.length],
          updated: date.toISOString(),
        });
      }

      individualMessagesDb[key] = messages;
    });
  }
}

seedIndividualMessages();

function messagesKey(passbookId: string, uniqueId: string) {
  return `${passbookId}:${uniqueId}`;
}

function isLeadInstalled(lead: Lead): boolean {
  return (
    (lead.deviceLibraryIdentifier?.length ?? 0) > 0 ||
    (lead.deviceToken?.length ?? 0) > 0 ||
    (lead.deviceGWallet?.length ?? 0) > 0
  );
}

function matchInstalledFilter(lead: Lead, filter: string): boolean {
  if (!filter) return true;
  const installed = isLeadInstalled(lead);
  if (filter === 'installed') return installed;
  if (filter === 'not_installed') return !installed;
  return true;
}

function matchCreatedRange(lead: Lead, from: string, to: string): boolean {
  if (!from && !to) return true;
  const created = lead.createdAt ? new Date(lead.createdAt).getTime() : 0;
  if (!created) return false;
  if (from) {
    const start = new Date(`${from}T00:00:00.000Z`).getTime();
    if (created < start) return false;
  }
  if (to) {
    const end = new Date(`${to}T23:59:59.999Z`).getTime();
    if (created > end) return false;
  }
  return true;
}

const SESSION_KEY = 'fixe_mock_session';
const IFRAME_BASE = '/mock-metabase.html';

export interface MockResult {
  status: number;
  data: unknown;
  contentType?: string;
}

/* ──────────────────────────── sessão (auth) ──────────────────────────── */

function getSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setSession(user: unknown) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(user));
  } catch {
    /* storage indisponível */
  }
}

function clearSession() {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    /* noop */
  }
}

/* ──────────────────────────── helpers ──────────────────────────── */

const ok = (data: unknown): MockResult => ({ status: 200, data });
const notFound = (): MockResult => ({ status: 404, data: { message: 'Não encontrado (mock).' } });

function mockAuthPayload(user: {
  role?: string;
  id_passbooks?: string | string[];
  keycloak_id?: string;
}) {
  const authSource = user.role === 'Nps' ? 'v2' : 'legacy';
  const storeId = firstPassbookId(user) ?? null;

  let start = '/selecionar-loja';
  if (authSource === 'v2' || user.role === 'Nps') start = '/nps';
  else if (user.role === 'Admin') start = '/clientes';
  else if (user.role === 'Owner') start = '/owner';
  else if (storeId) {
    if (user.role === 'Validator') start = `/loja/${storeId}/operador`;
    else if (user.role === 'Campaign') start = `/loja/${storeId}/campanhas/automatizadas`;
    else if (user.role === 'Operator') start = `/loja/${storeId}/leads`;
    else if (user.role === 'Manager') start = `/loja/${storeId}`;
    else if (user.role === 'Employee') start = '/selecionar-loja';
    else start = `/loja/${storeId}`; // Client etc.
  }

  return { user, csrfToken: 'mock-csrf', authSource, start };
}

const LEAD_COLUMNS = ['updated', 'createdAt', 'profile.name', 'profile.uniqueId', 'total_points', 'email', 'birthday', 'phone'];

function leadField(lead: Lead, field: string): string | number {
  switch (field) {
    case 'profile.name':
      return lead.profile?.name ?? '';
    case 'profile.uniqueId':
      return lead.profile?.uniqueId ?? '';
    case 'total_points':
      return lead.total_points ?? 0;
    case 'email':
      return lead.email ?? '';
    case 'birthday':
      return lead.birthday ?? '';
    case 'phone':
      return lead.phone ?? '';
    case 'createdAt':
      return lead.createdAt ?? '';
    default:
      return lead.updated ?? '';
  }
}

function fold(s: string): string {
  return s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

function parseProductValue(value: Product['value'] | undefined): number {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? 0));
  return Number.isFinite(n) ? n : 0;
}

function isProductEnabled(product: { enabled?: boolean | string }): boolean {
  return product.enabled !== false && product.enabled !== 'false';
}

function resolveIdPassbook(b: Record<string, unknown>): string {
  const direct = String(b.id_passbook ?? '');
  if (direct) return direct;
  const serial = String(b.serialNumber ?? '');
  if (!serial) return '';
  for (const [pid, list] of Object.entries(db.leads)) {
    if (list.some((l) => l.serialNumber === serial)) return pid;
  }
  return '';
}

function externalIdDedupError(): MockResult {
  return { status: 422, data: { status: '105', error: 'null transaction has already been made' } };
}

function checkResgateNaoPontua(idPassbook: string, externalId: string): MockResult | null {
  const passbook = db.passbooks[idPassbook];
  if (!passbook?.regraCartao?.resgateNaoPontua) return null;
  const reds = redemptionExternalIdsByPassbook.get(idPassbook);
  if (!reds) return null;
  const base = externalId.toLowerCase();
  for (const redId of reds) {
    if (redId.toLowerCase().includes(base)) {
      return { status: 422, data: { status: '105', error: 'Venda ignorada.' } };
    }
  }
  return null;
}

function registerRedemptionExternalId(idPassbook: string, externalId: string) {
  const set = redemptionExternalIdsByPassbook.get(idPassbook) ?? new Set<string>();
  set.add(externalId);
  redemptionExternalIdsByPassbook.set(idPassbook, set);
}

function checkAndRegisterExternalId(
  idPassbook: string,
  externalId: unknown,
  opts?: { isRedemption?: boolean; checkResgateNaoPontua?: boolean },
): MockResult | null {
  const ext = externalId == null ? '' : String(externalId).trim();
  if (!ext) return externalIdDedupError();

  if (opts?.checkResgateNaoPontua) {
    const blocked = checkResgateNaoPontua(idPassbook, ext);
    if (blocked) return blocked;
  }

  const key = `${idPassbook}:${ext}`;
  if (seenExternalIds.has(key)) return externalIdDedupError();

  seenExternalIds.add(key);
  if (opts?.isRedemption) registerRedemptionExternalId(idPassbook, ext);
  return null;
}

function filterRedemptionProducts(products: Product[], points: number): Product[] {
  return products.filter((pr) => {
    if (pr.redemption === false) return false;
    if (!isProductEnabled(pr)) return false;
    if (parseProductValue(pr.value) > points) return false;
    const type = (pr.redemptionType ?? 'product').toLowerCase();
    return type === 'product' || type === 'discount';
  });
}

function matchLead(lead: Lead, q: string): boolean {
  if (!q) return true;
  const needle = fold(q);
  return [lead.profile?.name, lead.profile?.uniqueId, lead.email, lead.phone, lead.storeName]
    .filter(Boolean)
    .some((v) => fold(String(v)).includes(needle));
}

function matchStoreFilter(lead: Lead, storeFilter: string): boolean {
  if (!storeFilter) return true;
  return (
    lead.storeName === storeFilter ||
    (lead.stores ?? []).includes(storeFilter)
  );
}

function metabaseFrames(passbook: Passbook | undefined, role = 'Admin') {
  const cfgs = passbook?.metabase ?? [{ dashboard: 1, aba: 'Painel' }];
  const passbookMulti = cfgs[0]?.multi ?? false;
  const multi = role === 'Operator' ? passbookMulti : role === 'Admin' ? false : passbookMulti;
  const lojaIndividual =
    typeof cfgs[0]?.lojaIndividual !== 'undefined' ? cfgs[0].lojaIndividual ?? null : null;
  const primaryLoja =
    multi ? null : lojaIndividual != null ? lojaIndividual : null;

  const frameUrl = (cfg: (typeof cfgs)[number], loja: string | null) => {
    const q = new URLSearchParams({ d: String(cfg.dashboard), aba: cfg.aba ?? 'Painel' });
    if (loja) q.set('loja', loja);
    return `${IFRAME_BASE}?${q.toString()}`;
  };

  const tabLoja = (cfg: (typeof cfgs)[number]) => {
    const ind =
      typeof cfg.lojaIndividual !== 'undefined' ? cfg.lojaIndividual ?? null : null;
    const multiLoop = cfg.multi ?? false;
    // Paridade com buildTabMetabaseParams / dashv2: só manda loja se a aba tiver multi.
    if (!multi && ind != null && multiLoop) return ind;
    return null;
  };

  const arrIframes = cfgs.map((c) => ({
    iframeUrl: frameUrl(c, tabLoja(c)),
    dashboard: c.dashboard,
    aba: c.aba,
  }));
  return {
    iframeUrl: frameUrl(cfgs[0], primaryLoja),
    arrIframes,
  };
}

function intervalSeries(days: number, seed: number) {
  const labels: string[] = [];
  const data: number[] = [];
  let total = 0;
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    labels.push(`${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`);
    const v = 5 + Math.floor((Math.sin(seed + i) + 1) * 30);
    data.push(v);
    total += v;
  }
  return { labels, data, totalPeriod: total };
}

/* ──────────────────────────── dispatcher ──────────────────────────── */

export async function handleMock(method: string, rawPath: string, body: unknown, formData?: FormData): Promise<MockResult> {
  // path relativo a /bff (sem querystring)
  const path = rawPath.split('?')[0].replace(/^\/bff\//, '');
  const seg = path.split('/').filter(Boolean);
  const b = (body ?? {}) as Record<string, unknown>;

  /* ---------- AUTH ---------- */
  if (path === 'auth/me') {
    const user = getSession();
    return user ? ok(mockAuthPayload(user)) : { status: 401, data: { message: 'Não autenticado.' } };
  }
  if (path === 'auth/login') {
    const user = userFromEmail(String(b.email ?? 'admin@fixe.me'));
    setSession(user);
    return ok(mockAuthPayload(user));
  }
  if (path === 'auth/logout') {
    clearSession();
    return { status: 204, data: null };
  }

  if (path === 'billing/quota' && method === 'GET') {
    const passbookId = new URL(rawPath, 'http://localhost').searchParams.get('passbookId') ?? '';
    return handleBillingQuota(passbookId);
  }
  const billing = handleBillingMock(method, path, body);
  if (billing) return billing;
  const segmentation = handleSegmentationMock(method, path, body);
  if (segmentation) return segmentation;
  const marketing = handleMarketingMock(method, path, body, new URL(rawPath, 'http://localhost').searchParams, db.passbooks);
  if (marketing) return marketing;
  const marketingAdmin = handleMarketingAdminMock(method, path, body);
  if (marketingAdmin) return marketingAdmin;

  /* ---------- METABASE ---------- */
  if (seg[0] === 'metabase' && seg[1] === 'profile') {
    return ok({ iframeUrl: `${IFRAME_BASE}?profile=${seg[2]}` });
  }
  if (seg[0] === 'metabase' && seg[1] === 'campaign' && seg[2] && seg[3]) {
    const passbook = db.passbooks[seg[2]];
    const dashboard = passbook?.hasPoints ? 133 : 55;
    return ok({
      iframeUrl: `${IFRAME_BASE}?d=${dashboard}&idcampanha=${encodeURIComponent(seg[3])}`,
    });
  }
  if (seg[0] === 'metabase') {
    const user = getSession();
    return ok(metabaseFrames(db.passbooks[seg[1]], user?.role));
  }

  /* ---------- OPERATOR BFF (/bff/operator/*) ---------- */
  if (seg[0] === 'operator' && seg[1] === 'preview' && method === 'POST') {
    const idPassbook = String(b.idPassbook ?? '');
    const passbook = db.passbooks[idPassbook];
    if (!passbook) return { status: 404, data: { message: 'Passbook não encontrado.' } };
    const result = previewOperatorBill({
      passbook,
      purchaseValue: Number(b.purchaseValue) || 0,
      redemptionAmount: Number(b.redemptionAmount) || 0,
      billDiscount: b.billDiscount != null ? Number(b.billDiscount) : undefined,
      productRedemption: Boolean(b.productRedemption),
      balance: b.balance != null ? Number(b.balance) : undefined,
    });
    return ok({ ...result, uniqueId: b.uniqueId ?? null, idPassbook });
  }

  /* ---------- INDIQUE E GANHE público (/bff/public/mgm/*) ---------- */
  const mgmPublic = handleMgmPublic(method, seg, b, db);
  if (mgmPublic) return mgmPublic;

  /* ---------- ÁREA DO PARTICIPANTE (/bff/public/participant/*) ---------- */
  const participant = handleParticipantPublic(method, seg, b, db);
  if (participant) return participant;

  /* ---------- PUBLIC INVITE (/bff/public/invite/*) ---------- */
  if (seg[0] === 'public' && seg[1] === 'invite') {
    const idPassbook = seg[2];
    const passbook = idPassbook ? db.passbooks[idPassbook] : undefined;

    if (seg.length === 3 && method === 'GET') {
      if (!passbook) return notFound();
      // Legado: `<%=client.name%> - <%=passbook.description%>` em toda view real de invite
      // (não há `client` separado no mock — reaproveita organizationName como nome do cliente).
      const clientName = passbook.organizationName;
      const description = passbook.description;
      const title = clientName && description ? `${clientName} - ${description}` : description || clientName || '';
      return ok({
        idPassbook,
        inactive: false,
        clientName,
        title,
        organizationName: passbook.organizationName,
        backgroundColor: passbook.backgroundColor,
        foregroundColor: passbook.foregroundColor,
        labelColor: passbook.labelColor,
        fieldUniqueIdLabel: passbook.fieldUniqueIdLabel || 'CPF',
        validationUniqueId: false,
        hasPreRegister: false,
        termsAndConditions:
          'Ao se cadastrar você concorda em receber comunicações da loja.\nSeus dados não serão compartilhados com terceiros.',
        loyaltyRules: [
          {
            label: 'Como funciona',
            value: `A cada compra você acumula pontos no cartão ${passbook.organizationName}.`,
          },
          { label: 'Resgate', value: 'Troque seus pontos por prêmios direto no caixa da loja.' },
        ],
        slug: passbook.slug,
        hasPT: passbook.hasPT,
        hasES: passbook.hasES,
        hasIN: passbook.hasIN,
        hasFlavor: passbook.hasFlavor,
        hasGWallet: passbook.hasGWallet,
        addFields: passbook.addFields ?? [],
        // 11 dígitos numéricos, igual ao real (generateNumericCode) — usado só na variação abf/sqg.
        generatedUniqueId: String(idPassbook)
          .split('')
          .reduce((acc, ch) => acc + ch.charCodeAt(0), 0)
          .toString()
          .padStart(11, '7')
          .slice(0, 11),
        hasGiftcard: Boolean(passbook.hasGiftcard),
        marketingChannels: { email: passbook.hasEmailMarketing === true, sms: passbook.hasSmsMarketing === true },
        // 11 chars alfanuméricos — usado só no fluxo `omatuto` (cartão-presente novo).
        generatedGiftCode: `GIFT${String(idPassbook).slice(-4)}`.padEnd(11, '0').slice(0, 11).toLowerCase(),
        ...participantInviteOverrides[idPassbook],
      });
    }

    if (seg[3] === 'validate-unique-id' && method === 'POST') {
      // Mock: só "não cadastrado" pra um valor mágico, pra dar pra testar os dois caminhos.
      if (String(b.uniqueId ?? '') === '00000000000') {
        return { status: 422, data: { message: 'Documento não cadastrado na plataforma.' } };
      }
      return ok({ status: 'ok' });
    }

    if (seg[3] === 'register-gift' && method === 'POST') {
      if (!passbook) return notFound();
      if (typeof b.website === 'string' && b.website.trim() !== '') return ok({ status: 'blocked' });

      const uniqueId = `giftmock-${idPassbook}`;
      const key = `${idPassbook}:${uniqueId}`;
      const existing = inviteRegistrations.get(key);
      if (existing) return ok({ status: 'duplicate', email: existing.email });

      const serialNumber = `MOCKSN${Date.now()}`;
      inviteRegistrations.set(key, { email: String(b.email ?? ''), serialNumber });
      const typeAccess = b.platform === 'ios' ? 'iOS' : 'Android';
      return ok({ status: 'created', idPassbook, serialNumber, typeAccess });
    }

    if (seg[3] === 'redeem-gift-card' && method === 'POST') {
      if (!passbook) return notFound();
      if (typeof b.website === 'string' && b.website.trim() !== '') return ok({ status: 'blocked' });

      // Mock: só este código "existe" pra testar o caminho de sucesso; qualquer outro é "não encontrado".
      const code = String(b.uniqueId ?? '').toUpperCase();
      if (code !== 'DEMO0000001') return ok({ status: 'not-found' });
      return ok({ status: 'emailed', email: String(b.email ?? 'sem-email@demo.fixe.me') });
    }

    if (seg[3] === 'states' && !seg[4] && method === 'GET') {
      return ok(Object.keys(MOCK_CITIES_BY_STATE).map((acronym) => ({ acronym })));
    }

    if (seg[3] === 'states' && seg[4] && method === 'GET') {
      return ok(MOCK_CITIES_BY_STATE[seg[4]] ?? []);
    }

    if (seg[3] === 'register' && method === 'POST') {
      if (!passbook) return notFound();
      if (typeof b.website === 'string' && b.website.trim() !== '') return ok({ status: 'blocked' });

      const uniqueId = String(b.uniqueId ?? '');
      const key = `${idPassbook}:${uniqueId}`;
      const existing = inviteRegistrations.get(key);
      if (existing) return ok({ status: 'duplicate', email: existing.email });

      const serialNumber = `MOCKSN${Date.now()}`;
      inviteRegistrations.set(key, { email: String(b.email ?? ''), serialNumber });
      const typeAccess = b.platform === 'ios' ? 'iOS' : 'Android';
      return ok({ status: 'created', idPassbook, serialNumber, typeAccess });
    }

    if (seg[3] === 'card' && seg[4] === 'apple' && method === 'GET') {
      return { status: 200, data: 'mock-pkpass', contentType: 'application/vnd.apple.pkpass' };
    }

    if (seg[3] === 'card' && seg[4] === 'google' && method === 'GET') {
      return ok('https://pay.google.com/gp/v/save/mock-token');
    }

    return notFound();
  }

  /* ---------- NPS (/bff/nps/*) ---------- */
  if (seg[0] === 'nps') {
    const query = new URL(rawPath, 'http://localhost').searchParams;
    return handleNps(method, seg.slice(1), body, query);
  }

  /* ---------- UPLOADS ---------- */
  if (path === 'uploads/banner') {
    const pid = String(formData?.get('passbookId') ?? '');
    const list = db.banners[pid];
    if (list) {
      const file = formData?.get('banner');
      const url = file instanceof File ? URL.createObjectURL(file) : `https://picsum.photos/seed/new-${Date.now()}/850/400`;
      list.push({
        _id: `${pid}-banner-${Date.now()}`,
        banner: url,
        title: String(formData?.get('title') ?? 'Novo banner'),
        description: String(formData?.get('description') ?? ''),
        url: String(formData?.get('url') ?? ''),
        active: true,
        order: list.length,
        entryDate: (formData?.get('entryDate') as string) || null,
        exitDate: (formData?.get('exitDate') as string) || null,
        expiry: formData?.get('expiry') === 'S',
      });
    }
    return ok({ success: true });
  }

  const fabUploadMatch = path.match(/^uploads\/fab\/([^/]+)$/);
  if (fabUploadMatch) {
    const pid = fabUploadMatch[1];
    const list = (db.fabButtons[pid] ??= []);
    const iconFile = formData?.get('icon');
    let iconUrl = 'data:image/svg+xml,' + encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#2563eb"/></svg>',
    );
    if (iconFile instanceof File) {
      const text = await iconFile.text();
      iconUrl = `data:image/svg+xml,${encodeURIComponent(text)}`;
    }
    list.push({
      _id: `${pid}-fab-${Date.now()}`,
      label: String(formData?.get('label') ?? 'Botão'),
      type: formData?.get('kind') === 'mgm' ? 'mgm' : 'link',
      value: String(formData?.get('url') ?? ''),
      color: String(formData?.get('backgroundColor') ?? '#2563eb'),
      active: formData?.get('active') !== 'false',
      launchDirect: formData?.get('launchDirect') !== 'false',
      order: list.length,
      iconUrl,
    });
    return ok({ success: true });
  }

  const fabImageMatch = path.match(/^uploads\/fab\/([^/]+)\/image$/);
  if (fabImageMatch && method === 'POST') {
    const fabId = fabImageMatch[1];
    const iconFile = formData?.get('icon');
    let iconUrl: string | undefined;
    if (iconFile instanceof File) {
      const text = await iconFile.text();
      iconUrl = `data:image/svg+xml,${encodeURIComponent(text)}`;
    }
    for (const list of Object.values(db.fabButtons)) {
      const fab = list.find((x) => x._id === fabId);
      if (fab && iconUrl) {
        fab.iconUrl = iconUrl;
        break;
      }
    }
    return ok({ success: true });
  }

  if (path === 'uploads/metabase-campaign') {
    const pid = String(formData?.get('id_passbook') ?? '');
    const bucket = (db.campaigns[pid] ??= { email: [], whatsapp: [], automation: [], upload: [] });
    bucket.upload.unshift({
      _id: `${pid}-camp-upload-${Date.now()}`,
      name: String(formData?.get('name') ?? 'Campanha BI'),
      description: String(formData?.get('description') ?? ''),
      updated: new Date().toISOString(),
      received: 0,
      users: Array.from({ length: 50 }, (_, k) => `SN${k}`),
      data_disparo: String(formData?.get('disparo_date') ?? ''),
      horario: String(formData?.get('disparo_time') ?? ''),
      performance: Number(formData?.get('performance') ?? 7),
    });
    return ok({ success: true });
  }

  if (path === 'uploads/campaign/image') {
    const file = formData?.get('image');
    const id = `${Date.now()}-${file instanceof File ? file.name : 'image.bin'}`;
    const url = file instanceof File ? URL.createObjectURL(file) : `https://picsum.photos/seed/${id}/400/200`;
    return ok({ error: 0, payload: { id, url } });
  }

  if (path.startsWith('assets/fab-icon')) {
    const url = new URL(rawPath, 'http://localhost').searchParams.get('url');
    if (!url) return { status: 400, data: { message: 'URL inválida.' } };
    if (url.startsWith('data:image/svg+xml,')) {
      const svg = decodeURIComponent(url.replace(/^data:image\/svg\+xml,/, ''));
      return { status: 200, data: svg, contentType: 'image/svg+xml' };
    }
    return { status: 404, data: { message: 'Ícone não encontrado.' } };
  }

  if (path.startsWith('assets/banner-image')) {
    const url = new URL(rawPath, 'http://localhost').searchParams.get('url');
    if (!url || !/amazonaws\.com\/banners\//i.test(url)) {
      return { status: 400, data: { message: 'URL inválida.' } };
    }
    // Em mock os banners usam picsum (URL direta); este path só existe para paridade com o BFF.
    return { status: 404, data: { message: 'Banner não encontrado.' } };
  }

  /* ---------- CATÁLOGO DE MODELOS (/bff/ai/models) ---------- */
  if (path === 'ai/models' && method === 'GET') return handleAiModels(getSession());

  /* ---------- LEGACY API (/bff/api/*) ---------- */
  if (seg[0] !== 'api') return notFound();
  const a = seg.slice(1); // segmentos após "api"
  const p = a.join('/');

  if (a[0] === 'observability' && a[1] === 'integrations' && method === 'GET') {
    return ok(queryObservability(new URL(rawPath, 'http://localhost').searchParams));
  }
  if (a[0] === 'observability' && a[1] === 'retention' && method === 'GET') {
    return ok(getObservabilityRetention());
  }
  if (a[0] === 'observability' && a[1] === 'retention' && method === 'PUT') {
    const saved = saveObservabilityRetention(b as { enabled?: boolean; intervalDays?: number });
    if (!saved) return { status: 400, data: { error: 'Informe um intervalo entre 1 e 365 dias.' } };
    return ok(saved);
  }

  // Permissões, Custos de IA e créditos do usuário
  const aiAdmin = handleAiAdmin(method, a, b, new URL(rawPath, 'http://localhost').searchParams, getSession(), db);
  if (aiAdmin) return aiAdmin;

  // Indique e Ganhe (admin)
  const mgmAdmin = handleMgmAdmin(method, a, b, new URL(rawPath, 'http://localhost').searchParams, db);
  if (mgmAdmin) return mgmAdmin;

  // passbook config — o GET real devolve o subdocumento, inclusive fabs.
  if (a[0] === 'passbook' && a[2] === 'tiers' && method === 'PUT') {
    const pb = db.passbooks[a[1]];
    if (!pb) return notFound();
    if (Array.isArray(b.tiers)) pb.tiers = b.tiers as Passbook['tiers'];
    if (typeof b.hasTiers === 'boolean') pb.hasTiers = b.hasTiers;
    if (typeof b.hasConfig === 'boolean') pb.hasConfig = b.hasConfig;
    if (b.barcodeFormat === 'QR' || b.barcodeFormat === 'CODE128')
      pb.barcodeFormat = b.barcodeFormat;
    return ok({ ok: true });
  }
  if (a[0] === 'user' && a[2] === 'tier-override' && (method === 'PUT' || method === 'DELETE')) {
    const lead = Object.values(db.leads)
      .flat()
      .find((l) => l._id === a[1]);
    if (!lead) return notFound();
    lead.tierOverride =
      method === 'PUT'
        ? {
            level: String(b.level ?? ''),
            reason: typeof b.reason === 'string' ? b.reason : undefined,
            setBy: 'mock',
            setAt: new Date().toISOString(),
          }
        : null;
    return ok({ ok: true });
  }
  if (a[0] === 'passbook' && a[2] === 'coupons' && method === 'PUT') {
    const pb = db.passbooks[a[1]];
    if (!pb) return notFound();
    pb.hasCoupons = b.hasCoupons === true;
    if (typeof b.couponName === 'string') pb.couponName = b.couponName.trim();
    if (typeof b.couponStoreUrl === 'string') pb.couponStoreUrl = b.couponStoreUrl.trim();
    return ok({ ok: true });
  }
  if (a[0] === 'passbook' && a[2] === 'varejonline' && (method === 'GET' || method === 'PUT')) {
    const pb = db.passbooks[a[1]];
    if (!pb) return notFound();
    if (method === 'PUT') varejonlineByPassbook.set(a[1], { active: b.active === true });
    const state = varejonlineByPassbook.get(a[1]);
    return ok({
      ok: true,
      active: state?.active === true,
      configured: true,
      enabled: true,
      pending: 0,
      failed: 0,
    });
  }
  if (a[0] === 'passbook' && a[2] === 'survey' && method === 'PUT') {
    const pb = db.passbooks[a[1]];
    if (!pb) return notFound();
    const regra = { ...(pb.regraCartao ?? {}) };
    regra.hasSurvey = b.hasSurvey === true;
    if (typeof b.idSurvey === 'string') regra.idSurvey = b.idSurvey;
    if (typeof b.messageSurvey === 'string') regra.messageSurvey = b.messageSurvey;
    if (typeof b.titleLinkSurvey === 'string') regra.titleLinkSurvey = b.titleLinkSurvey;
    if (typeof b.mensagemBonificacaoNPS === 'string') regra.mensagemBonificacaoNPS = b.mensagemBonificacaoNPS;
    if (typeof b.qtdeBonificarNPS === 'number') regra.qtdeBonificarNPS = b.qtdeBonificarNPS;
    pb.regraCartao = regra;
    return ok({ ok: true });
  }
  if (a[0] === 'passbook' && a[2] === 'back-fields' && method === 'PUT') {
    const pb = db.passbooks[a[1]];
    if (!pb) return notFound();
    const saved = pb as Passbook & Record<string, string | boolean>;
    for (const [key, value] of Object.entries(b)) {
      if (key === 'showMgmBackLink') {
        saved.showMgmBackLink = value === true;
        continue;
      }
      if (key.startsWith('field') && (typeof value === 'string' || value == null)) {
        saved[key] = value == null ? '' : value;
      }
    }
    return ok({ ok: true });
  }
  if (a[0] === 'passbook') {
    const pb = db.passbooks[a[1]];
    if (!pb) return notFound();
    const fabs = (db.fabButtons[pb._id] ?? []).map((fab) => ({
      kind: fab.type === 'mgm' ? 'mgm' : undefined,
      url: fab.value,
    }));
    return ok({ passbook: { ...pb, fabs } });
  }

  // lojas do usuário (seletor de loja / resolveStart)
  if (a[0] === 'passbookUser' && a[1] === 'user' && a[2] === 'find') {
    const sessionUser = getSession() as { role?: string; id_passbooks?: string | string[] } | null;
    const all = Object.values(db.passbooks).map((pb) => ({
      _id: pb._id,
      organizationName: pb.organizationName,
    }));
    // Operator/Validator/Campaign: só a loja do id_passbooks (não lista Tea Shop integrado)
    if (sessionUser?.id_passbooks && ['Operator', 'Validator', 'Campaign'].includes(sessionUser.role ?? '')) {
      const mineIds = passbookIdsOf(sessionUser);
      const mine = all.filter((p) => mineIds.includes(p._id));
      return ok({ passbooks: mine.length ? mine : all });
    }
    return ok({ passbooks: all });
  }

  /* ----- clientes (admin) ----- */
  if (p === 'clients') return ok(db.clients);

  /* ----- log de disparos (admin) ----- */
  if (a[0] === 'dispatch_runs') {
    const passbookId = db.clients[0]?.passbooks?.[0]?._id;
    const data = mockDispatchRuns(a.slice(1), new URL(rawPath, 'http://localhost').searchParams, passbookId ? String(passbookId) : undefined);
    return data ? ok(data) : notFound();
  }
  if (a[0] === 'client' && a[1] === 'inactive') {
    const c = db.clients.find((x) => x._id === a[2]);
    if (c) c.inactive = !c.inactive;
    return ok({ success: true });
  }
  if (a[0] === 'client' && a.length === 3 && method === 'DELETE') {
    db.clients = db.clients.filter((x) => x._id !== a[1]);
    return ok({ success: true });
  }
  if (a[0] === 'client' && a.length === 2 && method === 'GET') {
    const c = db.clients.find((x) => x._id === a[1]);
    return c ? ok({ client: c }) : notFound();
  }
  if (p === 'client' && method === 'POST') {
    // edição/criação — apenas confirma (a UI recarrega via /clients)
    return ok({ success: true });
  }
  if (p === 'auth/register' && method === 'POST') {
    return ok({ user: { _id: `user_new_${Date.now()}` } });
  }
  if (a[0] === 'user' && a.length === 2 && method === 'GET') {
    const u = db.users[a[1]];
    return u ? ok(u) : ok({ _id: a[1], email: '', profile: { name: '' } });
  }
  if (a[0] === 'user' && a[2] === 'observacao' && method === 'PUT') {
    const leadId = a[1];
    const observacao = String(b.observacao ?? '');
    for (const list of Object.values(db.leads)) {
      const lead = list.find((l) => l._id === leadId);
      if (lead) {
        lead.observacao = observacao;
        return ok({ status: 'OK' });
      }
    }
    return notFound();
  }

  /* ----- estatísticas ----- */
  if (a[0] === 'signed_up' && a[1] === 'user') return ok({ serialNumbers: db.stats[a[2]]?.serialNumbers ?? 0 });
  if (a[0] === 'signed_up_user') return ok({ serialNumbers: db.stats[a[1]]?.serialNumbers ?? 0 });
  if (a[0] === 'platform_user' && a[1] && a[2]) {
    const pid = a[1];
    const typeAccess = decodeURIComponent(a[2]);
    const all = db.leads[pid] ?? [];
    const count = all.filter((l) => {
      if (!l.profile?.name) return false;
      if (typeAccess === 'iOS' || typeAccess === 'APP-ios') {
        if (typeAccess === 'APP-ios') {
          return (l.deviceToken ?? []).some(
            (t) => typeof t === 'object' && t !== null && (t as { typeAccess?: string }).typeAccess === 'APP-ios',
          );
        }
        return (l.deviceLibraryIdentifier?.length ?? 0) > 0;
      }
      if (typeAccess === 'Android' || typeAccess === 'APP-android') {
        if (typeAccess === 'APP-android') {
          return (l.deviceToken ?? []).some(
            (t) => typeof t === 'object' && t !== null && (t as { typeAccess?: string }).typeAccess === 'APP-android',
          );
        }
        // "Android" legado ≈ Google Wallet + typeAccess Android com device
        return (
          (l.deviceGWallet?.length ?? 0) > 0 ||
          (l.typeAccess === 'Android' &&
            ((l.deviceToken?.length ?? 0) > 0 || (l.deviceGWallet?.length ?? 0) > 0))
        );
      }
      return false;
    }).length;
    return ok({ platform: count });
  }
  if (a[0] === 'signed_up') return ok(db.stats[a[1]] ?? { serialNumbers: 0, android: 0, ios: 0 });
  if (a[0] === 'stores' && a[1] && method === 'GET') {
    return ok(db.stores[a[1]] ?? []);
  }
  if (a[0] === 'registration' && a[1] && method === 'GET' && a[1] !== 'save') {
    for (const pid of Object.keys(db.leads)) {
      const found = db.leads[pid]?.find((l) => l._id === a[1]);
      if (found) return ok({ user: found });
    }
    return notFound();
  }
  if (a[0] === 'log' && a[1] && method === 'GET') {
    const serial = a[1];
    for (const pid of Object.keys(db.leads)) {
      const found = db.leads[pid]?.find((l) => l.serialNumber === serial);
      if (found?.logs) return ok(found.logs);
    }
    return ok([]);
  }
  if (a[0] === 'lastStamp') return ok({ last_date: new Date().toISOString() });

  /* ----- leads (paginação) ----- */
  if (p === 'usersPaginate' || p === 'usersPaginateMulti') {
    const pid = String(b.id_passbook ?? '');
    const all = db.leads[pid] ?? [];
    const search = ((b.search as { value?: string })?.value ?? '').toString();
    const storeFilter = String(b.storeFilter ?? '');
    const installedFilter = String(b.installedFilter ?? '');
    const createdFrom = String(b.createdFrom ?? '');
    const createdTo = String(b.createdTo ?? '');
    const filtered = all.filter(
      (l) =>
        matchLead(l, search) &&
        matchStoreFilter(l, storeFilter) &&
        matchInstalledFilter(l, installedFilter) &&
        matchCreatedRange(l, createdFrom, createdTo),
    );

    const order = (b.order as Array<{ column: number; dir: 'asc' | 'desc' }>) ?? [{ column: 0, dir: 'desc' }];
    const { column, dir } = order[0] ?? { column: 0, dir: 'desc' };
    const field = LEAD_COLUMNS[column] ?? 'updated';
    const sorted = [...filtered].sort((x, y) => {
      const vx = leadField(x, field);
      const vy = leadField(y, field);
      const cmp =
        typeof vx === 'number' && typeof vy === 'number'
          ? vx - vy
          : String(vx).localeCompare(String(vy), 'pt-BR', { sensitivity: 'base' });
      return dir === 'desc' ? -cmp : cmp;
    });

    const skip = Number(b.skip ?? 0);
    const length = Number(b.length ?? 25);
    const data = length === 0 ? [] : sorted.slice(skip, skip + length);
    return ok({ data, recordsFiltered: filtered.length, recordsTotal: all.length });
  }

  // busca de leads (operador) / fetchLead por _id
  if (p === 'users' && method === 'POST') {
    const pid = String(b.id_passbook ?? '');
    const all = db.leads[pid] ?? [];
    const q = String(b.query ?? '');
    const byId = all.find((l) => l._id === q);
    if (byId) return ok([byId]);
    return ok(all.filter((l) => matchLead(l, q)).slice(0, 20));
  }

  // salvar lead (editar/excluir)
  if (a[0] === 'registration' && a[1] === 'save') {
    const pid = a[2];
    const list = db.leads[pid] ?? [];
    const idx = list.findIndex((l) => l._id === (b._id as string));
    if (idx >= 0) {
      const profile = (b.profile as { name?: string; uniqueId?: string }) ?? {};
      if (profile.name === '') {
        list.splice(idx, 1); // exclusão de cadastro
      } else {
        list[idx] = {
          ...list[idx],
          profile: { ...list[idx].profile, ...profile },
          email: (b.email as string) ?? list[idx].email,
          phone: (b.phone as string) ?? list[idx].phone,
          birthday: (b.birthday as string) ?? list[idx].birthday,
        };
      }
    }
    return ok({ success: true });
  }

  if (a[0] === 'messages-individuals' && a[1] && a[2] && method === 'GET') {
    const key = messagesKey(a[1], decodeURIComponent(a[2]));
    return ok(individualMessagesDb[key] ?? []);
  }

  if (p === 'registration/message' && method === 'POST') {
    const pid = String(b.id_passbook ?? '');
    const serial = String(b.serialNumber ?? '');
    const uniqueId = String(b.uniqueId ?? '');
    const description = String(b.description ?? '');
    const list = db.leads[pid] ?? [];
    const idx = list.findIndex((l) => l.serialNumber === serial);
    if (idx >= 0) {
      list[idx] = {
        ...list[idx],
        profile: {
          ...list[idx].profile,
          uniqueId: uniqueId || list[idx].profile?.uniqueId,
        },
        email: b.email != null && b.email !== '' ? String(b.email) : list[idx].email,
        phone: b.phone != null && b.phone !== '' ? String(b.phone) : list[idx].phone,
        birthday: b.birthday != null && b.birthday !== '' ? String(b.birthday) : list[idx].birthday,
        gender: b.gender != null && b.gender !== '' ? String(b.gender) : list[idx].gender,
        observacao: b.observacao != null ? String(b.observacao) : list[idx].observacao,
        updated: new Date().toISOString(),
      };
    }
    if (uniqueId && description) {
      const key = messagesKey(pid, uniqueId);
      const entry: IndividualMessage = {
        _id: `msg-${Date.now()}`,
        id_passbook: pid,
        uniqueId,
        description,
        updated: new Date().toISOString(),
      };
      individualMessagesDb[key] = [entry, ...(individualMessagesDb[key] ?? [])];
    }
    return ok({ status: 'OK' });
  }

  if (a[0] === 'user' && a[1] && a[2] && method === 'DELETE') {
    const pid = a[1];
    const leadId = a[2];
    const list = db.leads[pid] ?? [];
    const idx = list.findIndex((l) => l._id === leadId);
    if (idx >= 0) list.splice(idx, 1);
    return ok({ success: true });
  }

  if (p === 'mailResend' && method === 'POST') {
    const serial = String(b.serial_number ?? '');
    for (const list of Object.values(db.leads)) {
      const lead = list.find((l) => l.serialNumber === serial);
      if (lead) {
        lead.lastCardResendAt = new Date().toISOString();
        break;
      }
    }
    return ok({ success: true, status: 'ok' });
  }
  if (p === 'mailResend') return ok({ success: true });
  if (a[0] === 'export') return ok({ success: true, message: 'Exportação enviada (mock).' });

  /* ----- produtos ----- */
  if (a[0] === 'productsNotRedemption' && a[1] && method === 'GET') {
    const idPassbook = a[1];
    const products = (db.products[idPassbook] ?? []).filter((pr) => pr.redemption === false);
    return ok(products);
  }
  if (a[0] === 'products' && a[1] && method === 'GET') {
    const idPassbook = a[1];
    const all = db.products[idPassbook] ?? [];
    if (a[2] === 'true') return ok(all);
    if (a[2] === 'false' && a[3] !== undefined) {
      const points = parseFloat(a[3]) || 0;
      return ok(filterRedemptionProducts(all, points));
    }
    // Legado GET /products/:id — exclui value "0"
    return ok(all.filter((pr) => parseProductValue(pr.value) !== 0));
  }
  if (p === 'product' && method === 'POST') {
    const pid = String(b.idPassbook ?? '');
    const list = (db.products[pid] ??= []);
    const action = b.action as string;
    if (action === 'insert') {
      list.push({
        _id: `${pid}-prod-${Date.now()}`,
        uniqueId: String(b.uniqueId ?? ''),
        name: String(b.name ?? ''),
        value: Number(b.value ?? 0),
        price: Number(b.price ?? 0),
        redemption: Boolean(b.redemption),
        redemptionType: b.redemptionType as never,
        idPassbook: pid,
      });
    } else {
      const idx = list.findIndex((x) => x._id === b.id);
      if (idx >= 0) {
        if (action === 'deleted') list.splice(idx, 1);
        else
          list[idx] = {
            ...list[idx],
            name: String(b.name ?? list[idx].name),
            value: Number(b.value ?? list[idx].value),
            price: Number(b.price ?? list[idx].price),
            redemption: Boolean(b.redemption),
            redemptionType: b.redemptionType as never,
          };
      }
    }
    return ok({ success: true });
  }

  /* ----- banners ----- */
  if (a[0] === 'banners' && a.length === 2 && method === 'POST') {
    const pid = a[1];
    const all = [...(db.banners[pid] ?? [])].sort((x, y) => (x.order ?? 0) - (y.order ?? 0));
    const search = ((b.search as { value?: string })?.value ?? '').toString().toLowerCase();
    const filtered = search
      ? all.filter((x) => (x.title ?? '').toLowerCase().includes(search) || (x.description ?? '').toLowerCase().includes(search))
      : all;
    const skip = Number(b.skip ?? 0);
    const length = Number(b.length ?? 25);
    return ok({ data: filtered.slice(skip, skip + length), filtered: filtered.length, total: all.length });
  }
  if (a[0] === 'banners' && a[2] === 'order' && method === 'PUT') {
    const pid = String(b.passbookId ?? '');
    const dir = b.direction as 'up' | 'down';
    const list = db.banners[pid] ?? [];
    const idx = list.findIndex((x) => x._id === a[1]);
    const swap = dir === 'up' ? idx - 1 : idx + 1;
    if (idx >= 0 && swap >= 0 && swap < list.length) {
      [list[idx], list[swap]] = [list[swap], list[idx]];
    }
    list.forEach((item, i) => {
      item.order = i + 1;
    });
    return ok({ success: true });
  }
  if (a[0] === 'banners' && a.length === 2 && method === 'PATCH') {
    for (const list of Object.values(db.banners)) {
      const banner = list.find((x) => x._id === a[1]);
      if (banner) {
        if (typeof b.active === 'boolean') banner.active = b.active;
        if (typeof b.title === 'string') banner.title = b.title;
        if (typeof b.description === 'string') banner.description = b.description;
        if (typeof b.url === 'string') banner.url = b.url;
        if (typeof b.expiry === 'boolean') banner.expiry = b.expiry;
        if ('entryDate' in b) banner.entryDate = (b.entryDate as string) || null;
        if ('exitDate' in b) banner.exitDate = (b.exitDate as string) || null;
        break;
      }
    }
    return ok({ success: true });
  }
  if (a[0] === 'banners' && a.length === 2 && method === 'DELETE') {
    for (const [pid, list] of Object.entries(db.banners)) {
      const idx = list.findIndex((x) => x._id === a[1]);
      if (idx >= 0) {
        list.splice(idx, 1);
        list.forEach((item, i) => {
          item.order = i + 1;
        });
        void pid;
        break;
      }
    }
    return ok({ success: true });
  }

  /* ----- botões flutuantes (FAB) — espelha /fabs/* ----- */
  if (a[0] === 'fabs' && a[1] === 'data' && a[2] && method === 'POST') {
    const pid = a[2];
    const all = [...(db.fabButtons[pid] ?? [])].sort((x, y) => (x.order ?? 0) - (y.order ?? 0));
    const search = ((b.search as { value?: string })?.value ?? '').toString().toLowerCase();
    const filtered = search
      ? all.filter((x) => x.label.toLowerCase().includes(search) || x.value.toLowerCase().includes(search))
      : all;
    const skip = Number(b.skip ?? 0);
    const length = Number(b.length ?? 25);
    const data = filtered.slice(skip, skip + length).map((fab) => ({
      _id: fab._id,
      label: fab.label,
      url: fab.value.startsWith('http') || fab.value.startsWith('tel:') || fab.value.startsWith('mailto:')
        ? fab.value
        : `https://wa.me/${fab.value.replace(/\D/g, '')}`,
      backgroundColor: fab.color,
      foregroundColor: '#ffffff',
      active: fab.active,
      launchDirect: fab.launchDirect ?? true,
      order: fab.order,
      icon: fab.iconUrl,
      ...(fab.type === 'mgm' ? { kind: 'mgm', shouldShow: true } : {}),
    }));
    return ok({ data, filtered: filtered.length, total: all.length });
  }
  if (a[0] === 'fabs' && a[2] === 'order' && method === 'PATCH') {
    const pid = String(b.passbookId ?? '');
    const dir = b.direction as 'up' | 'down';
    const list = db.fabButtons[pid] ?? [];
    const idx = list.findIndex((x) => x._id === a[1]);
    const swap = dir === 'up' ? idx - 1 : idx + 1;
    if (idx >= 0 && swap >= 0 && swap < list.length) {
      [list[idx], list[swap]] = [list[swap], list[idx]];
    }
    list.forEach((item, i) => {
      item.order = i + 1;
    });
    return ok({ success: true });
  }
  if (a[0] === 'fabs' && a.length === 2 && method === 'PATCH') {
    for (const list of Object.values(db.fabButtons)) {
      const fab = list.find((x) => x._id === a[1]);
      if (fab) {
        if (typeof b.active === 'boolean') fab.active = b.active;
        if (typeof b.launchDirect === 'boolean') fab.launchDirect = b.launchDirect;
        if (b.label) fab.label = String(b.label);
        if (b.url) fab.value = String(b.url);
        if (b.backgroundColor) fab.color = String(b.backgroundColor);
        if (b.kind === 'mgm') fab.type = 'mgm';
        else if (b.kind === '') fab.type = 'link';
        break;
      }
    }
    return ok({ success: true });
  }
  if (a[0] === 'fabs' && a.length === 2 && method === 'DELETE') {
    for (const [pid, list] of Object.entries(db.fabButtons)) {
      const idx = list.findIndex((x) => x._id === a[1]);
      if (idx >= 0) {
        list.splice(idx, 1);
        list.forEach((item, i) => {
          item.order = i + 1;
        });
        db.fabButtons[pid] = list;
        break;
      }
    }
    return ok({ success: true });
  }

  /* ----- legado fab-buttons (deprecado) ----- */
  if (a[0] === 'fab-buttons' && a.length === 2 && method === 'POST') {
    const all = [...(db.fabButtons[a[1]] ?? [])].sort((x, y) => (x.order ?? 0) - (y.order ?? 0));
    const search = ((b.search as { value?: string })?.value ?? '').toString().toLowerCase();
    const filtered = search
      ? all.filter((x) => x.label.toLowerCase().includes(search) || x.value.toLowerCase().includes(search))
      : all;
    const skip = Number(b.skip ?? 0);
    const length = Number(b.length ?? 25);
    return ok({ data: filtered.slice(skip, skip + length), filtered: filtered.length, total: all.length });
  }
  if (p === 'fab-button' && method === 'POST') {
    const pid = String(b.idPassbook ?? '');
    const list = (db.fabButtons[pid] ??= []);
    if (b.action === 'edit' && b.id) {
      const fab = list.find((x) => x._id === b.id);
      if (fab) {
        Object.assign(fab, {
          label: b.label ?? fab.label,
          type: b.type ?? fab.type,
          value: b.value ?? fab.value,
          color: b.color ?? fab.color,
          active: typeof b.active === 'boolean' ? b.active : fab.active,
        });
      }
    } else {
      list.push({
        _id: `${pid}-fab-${Date.now()}`,
        label: String(b.label ?? 'Botão'),
        type: b.type as never,
        value: String(b.value ?? ''),
        color: b.color as string,
        active: typeof b.active === 'boolean' ? b.active : true,
        order: list.length,
      });
    }
    return ok({ success: true });
  }
  if (a[0] === 'fab-buttons' && a[2] === 'order' && method === 'PUT') {
    const pid = String(b.passbookId ?? '');
    const dir = b.direction as 'up' | 'down';
    const list = db.fabButtons[pid] ?? [];
    const idx = list.findIndex((x) => x._id === a[1]);
    const swap = dir === 'up' ? idx - 1 : idx + 1;
    if (idx >= 0 && swap >= 0 && swap < list.length) {
      [list[idx], list[swap]] = [list[swap], list[idx]];
    }
    list.forEach((item, i) => {
      item.order = i + 1;
    });
    return ok({ success: true });
  }
  if (a[0] === 'fab-buttons' && a.length === 2 && method === 'PATCH') {
    for (const list of Object.values(db.fabButtons)) {
      const fab = list.find((x) => x._id === a[1]);
      if (fab) {
        fab.active = Boolean(b.active);
        break;
      }
    }
    return ok({ success: true });
  }
  if (a[0] === 'fab-buttons' && a.length === 2 && method === 'DELETE') {
    for (const [pid, list] of Object.entries(db.fabButtons)) {
      const idx = list.findIndex((x) => x._id === a[1]);
      if (idx >= 0) {
        list.splice(idx, 1);
        db.fabButtons[pid] = list;
        break;
      }
    }
    return ok({ success: true });
  }

  /* ----- campanhas ----- */
  if (a[0] === 'campaign' && a[1] && a[2] === 'report' && method === 'GET') {
    const DAY = 86_400_000;
    const now = Date.now();
    const days = 30;
    const daily = Array.from({ length: 14 }, (_, i) => {
      const d = new Date(now - (13 - i) * DAY);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const delivered = 180 + ((i * 37) % 60);
      const orders = i % 3 === 0 ? 0 : 4 + (i % 5);
      return { date: key, sent: delivered + 6, delivered, failed: 6, orders, revenue: orders * 58.4 };
    });
    const buyers = Array.from({ length: 24 }, (_, i) => ({
      userId: `65a0000000000000000000${String(i).padStart(2, '0')}`,
      name: `Cliente ${i + 1}`,
      idPassbook: '',
      uniqueId: `000.000.0${String(i).padStart(2, '0')}-00`,
      gender: i % 2 === 0 ? 'F' : 'M',
      orders: 1 + (i % 3),
      revenue: 40 + i * 7.5,
      receivedAt: new Date(now - (6 + (i % 5)) * DAY).toISOString(),
      firstPurchaseAt: new Date(now - (5 + (i % 5)) * DAY).toISOString(),
      lastPurchaseAt: new Date(now - (3 + (i % 3)) * DAY).toISOString(),
    }));
    const orders = daily.reduce((n, d) => n + d.orders, 0);
    const revenue = daily.reduce((n, d) => n + d.revenue, 0);
    const delivered = daily.reduce((n, d) => n + d.delivered, 0);
    return ok({
      campaign: { id: a[1], nome: 'Campanha de exemplo', id_passbook: '' },
      windowDays: 7,
      period: { from: new Date(now - days * DAY).toISOString(), to: new Date(now).toISOString(), days },
      generatedAt: new Date(now).toISOString(),
      totals: {
        dispatches: daily.length,
        audience: 460,
        recipients: 420,
        reachRate: 91.3,
        sent: delivered + 84,
        delivered,
        failed: 84,
        deliveryRate: 96.9,
        buyers: buyers.length,
        conversionRate: 5.71,
        orders,
        ordersWithValue: orders,
        reversedOrders: 1,
        revenue,
        avgTicket: orders ? revenue / orders : 0,
      },
      dispatches: daily.map((d, i) => ({
        id: `d${i}`,
        date: new Date(now - (13 - i) * DAY).toISOString(),
        status: 'success',
        error: null,
        tipo_disparo: 'push',
        sent: d.sent,
        delivered: d.delivered,
        failed: d.failed,
      })),
      daily,
      purchaseLag: [0, 1, 2, 3, 4, 5, 6, 7].map((day) => ({ day, orders: Math.max(1, 12 - day * 2), buyers: Math.max(1, 9 - day) })),
      buyers,
      buyersTruncated: false,
    });
  }
  if (a[0] === 'messages' && a[1]) return ok(db.campaigns[a[1]]?.email ?? []);
  if (a[0] === 'messages-wp' && a[1] === 'logs') return ok(db.campaigns[a[2]]?.whatsapp ?? []);
  if (a[0] === 'campaigns' && a[1]) {
    const passbookId = a[1];
    const seed = Math.max(0, Object.keys(db.passbooks).indexOf(passbookId));
    // Regenera com datas relativas ao agora — Agenda "Hoje" sempre tem itens.
    // Pausa, exclusão e o horário/mensagem da aniversário feitos nesta sessão ficam.
    const previous = db.campaigns[passbookId]?.automation ?? [];
    const previousById = new Map(previous.map((c) => [c._id, c]));
    const generated = buildAutomationCampaigns(passbookId, seed).filter((c) => !removedAutomationIds.has(c._id));
    const generatedIds = new Set(generated.map((c) => c._id));
    const createdHere = previous.filter((c) => !generatedIds.has(c._id) && !removedAutomationIds.has(c._id));
    const rows = [...createdHere, ...generated.map((row) => {
      const old = previousById.get(row._id);
      if (!old) return row;
      return {
        ...row,
        active: old.active,
        name: old.name,
        message: old.message,
        description: old.description,
        frequencia_horas: old.frequencia_horas,
      };
    })];
    if (db.campaigns[passbookId]) db.campaigns[passbookId].automation = rows;
    return ok(
      rows.map((c) => ({
        _id: c._id,
        id_passbook: passbookId,
        nome: c.name,
        mensagem: c.message ?? c.description,
        created_at: c.created_at ?? c.updated,
        ativo: c.active !== false,
        total_recebidos: c.received ?? 0,
        atividade: c.atividade ?? '',
        frequencia: c.frequencia ?? '1',
        frequencia_horas: c.frequencia_horas,
        frequencia_unico: c.frequencia_unico,
        frequencia_dia: c.frequencia_dia,
        frequencia_dia_semana: c.frequencia_dia_semana,
        expirar: c.expirar,
        tipo_disparo: c.tipo_disparo ?? 'push',
        tipo_campanha: c.tipo_campanha ?? 'C',
        plataforma: c.plataforma ?? '',
        genero: c.genero ?? '',
      })),
    );
  }
  if (a[0] === 'metabase-campaigns' && a[1]) {
    return ok(db.campaigns[a[1]]?.upload ?? []);
  }
  if (a[0] === 'campaign' && a[1] && method === 'GET') {
    const row = Object.values(db.campaigns)
      .flatMap((c) => c.automation)
      .find((c) => c._id === a[1]);
    if (!row) return { status: 404, data: { message: 'Campanha não encontrada' } };
    return ok({
      campaign: {
        _id: row._id,
        nome: row.name,
        mensagem: row.message ?? row.description,
        ativo: row.active !== false,
        tipo_campanha: row.tipo_campanha ?? 'C',
        tipo_disparo: row.tipo_disparo ?? 'push',
        frequencia: row.frequencia ?? '1',
        frequencia_horas: row.frequencia_horas,
        frequencia_unico: row.frequencia_unico,
        atividade: row.atividade,
        expirar: row.expirar,
      },
    });
  }
  if (p === 'user/filter' && method === 'POST') {
    return ok({ result: 42 + Math.floor(Math.random() * 80) });
  }
  if (a[0] === 'user' && a[1] === 'listByIdPassbook' && a[2]) {
    return ok(db.leads[a[2]] ?? []);
  }
  if (a[0] === 'stores' && a[1]) return ok([]);
  if (a[0] === 'clusters' && a[1]) return ok([]);
  if (a[0] === 'state' && a[1] === 'carregar-estados') return ok([]);
  if (p === 'campaign' && method === 'POST') {
    const pid = String(b.id_passbook ?? '');
    const bucket = (db.campaigns[pid] ??= { email: [], whatsapp: [], automation: [], upload: [] });
    if (b.action === 'pause' && b.idcampanha) {
      const automation = bucket.automation.find((c) => c._id === b.idcampanha);
      if (automation && typeof b.ativo === 'boolean') automation.active = b.ativo;
      return ok({ success: true });
    }
    if (b.action === 'delete' && b.idcampanha) {
      const automation = bucket.automation.find((c) => c._id === b.idcampanha);
      if (automation?.tipo_campanha === 'A') {
        return { status: 403, data: { error: 'A campanha de aniversário não pode ser excluída. Pause ela.' } };
      }
      removedAutomationIds.add(String(b.idcampanha));
      bucket.automation = bucket.automation.filter((c) => c._id !== b.idcampanha);
      return ok({ success: true });
    }
    if (b.action === 'edit' && b.idcampanha) {
      const automation = bucket.automation.find((c) => c._id === b.idcampanha);
      if (automation && automation.tipo_campanha !== 'A') {
        return { status: 403, data: { error: 'Campanha automatizada não pode ser editada. Pause ou exclua.' } };
      }
      if (automation) {
        Object.assign(automation, {
          message: b.mensagem ?? automation.message,
          description: b.mensagem ?? automation.description,
          frequencia_horas: b.frequencia_horas ?? automation.frequencia_horas,
          updated: new Date().toISOString(),
        });
      }
    } else {
      bucket.automation.unshift({
        _id: `${pid}-camp-automation-${Date.now()}`,
        name: String(b.nome ?? 'Automação'),
        description: String(b.mensagem ?? ''),
        message: String(b.mensagem ?? ''),
        created_at: new Date().toISOString(),
        updated: new Date().toISOString(),
        received: 0,
        status: 'active',
        active: typeof b.ativo === 'boolean' ? b.ativo : true,
        tipo_disparo: String(b.tipo_disparo ?? 'push'),
        frequencia: String(b.frequencia ?? '1'),
        atividade: String(b.atividade ?? ''),
        frequencia_unico: String(b.frequencia_unico ?? ''),
        frequencia_horas: String(b.frequencia_horas ?? ''),
        frequencia_dia: String(b.frequencia_dia ?? ''),
        frequencia_dia_semana: String(b.frequencia_dia_semana ?? ''),
        expirar: Boolean(b.expirar),
      });
    }
    return ok({ success: true });
  }
  if (p === 'message' && method === 'POST') {
    const pid = String(b.id_passbook ?? '');
    if (!pid) return { status: 400, data: { message: 'id_passbook obrigatório' } };
    const bucket = (db.campaigns[pid] ??= { email: [], whatsapp: [], automation: [], upload: [] });
    bucket.email.unshift({
      _id: `${pid}-camp-email-${Date.now()}`,
      name: String(b.name ?? 'Campanha'),
      description: String(b.description ?? ''),
      message: String(b.description ?? ''),
      updated: new Date().toISOString(),
      received: 0,
      status: 'sent',
    });
    return ok({ success: true });
  }
  if (p === 'message-wp' && method === 'POST') {
    const pid = String(b.id_passbook ?? '');
    if (!pid) return { status: 400, data: { message: 'id_passbook obrigatório' } };
    const bucket = (db.campaigns[pid] ??= { email: [], whatsapp: [], automation: [], upload: [] });
    bucket.whatsapp.unshift({
      _id: `${pid}-camp-wp-${Date.now()}`,
      name: String(b.name ?? 'Disparo'),
      description: String(b.description ?? ''),
      message: String(b.description ?? ''),
      updated: new Date().toISOString(),
      received: 0,
      status: 'queued',
      logs: [],
    });
    return ok({ success: true });
  }
  if (a[0] === 'message' && a[1] === 'remove') {
    for (const c of Object.values(db.campaigns)) {
      c.email = c.email.filter((x) => x._id !== a[2]);
    }
    return ok({ success: true });
  }

  /* ----- owner ----- */
  if (p === 'owner' && method === 'POST') {
    const passbooks = db.clients.map((c) => ({
      name: c.name,
      passbooks: (c.passbooks ?? []).map((pb) => ({
        _id: pb._id,
        backgroundColor: pb.backgroundColor,
        hasGiftcard: db.passbooks[pb._id]?.hasGiftcard,
      })),
    }));
    const total = db.clients.length;
    const resgates = Object.values(db.leads).flat().reduce((acc) => acc + 1, 0);
    return ok({ passbooks, total, resgates });
  }
  if (a[0] === 'owner' && a[1] === 'export') return ok({ success: true });

  /* ----- dashboard ----- */
  if (p === 'dashboard/consolidated-numbers') {
    const pid = (b.passbooks as string[])?.[0] ?? '';
    const seed = pid.length;
    return ok({
      data: [
        { action: 'input', count: 1200 + seed * 7, sumPrice: 86000 + seed * 1000, sumPointsChanged: 18000 + seed * 100, redemption: false },
        { action: 'resgate', count: 90 + seed, sumPointsChanged: 4200 + seed * 50, redemption: true },
      ],
    });
  }
  if (a[0] === 'user' && a[1] === 'by_interval_add') return ok(intervalSeries(Number(a[3] ?? 7), 1));
  if (a[0] === 'transaction_by_interval') return ok(intervalSeries(Number(a[3] ?? 7), 2));
  if (a[0] === 'gender') {
    return ok([
      { genero: 'Masculino', soma: 540 },
      { genero: 'Feminino', soma: 615 },
      { genero: null, soma: 92 },
    ]);
  }
  if (a[0] === 'mediaIdade') return ok(34);

  /* ----- operador ----- */
  if (a[0] === 'transaction_current_day_redemption') {
    // Shape real da API: array do aggregate
    return ok([{ totalAmount: 7, total_day: 7 }]);
  }
  if (a[0] === 'transaction_current_day') {
    return ok([{ _id: null, totalAmount: 1875.4, total_day: 1875.4, total_qtde: 42 }]);
  }
  if (a[0] === 'rewards' && a[1] && a[2] === 'true' && method === 'GET') {
    return ok(3);
  }
  if (a[0] === 'transaction' && a[1] === 'simulate') {
    const idPassbook = String(b.id_passbook ?? '');
    const passbook = db.passbooks[idPassbook];
    const purchase = Number(b.value) || 0;
    if (!passbook) return { status: 422, data: { status: '103' } };

    const details = Array.isArray(b.details) ? b.details : [];
    const rc = passbook.regraCartao;

    // regra-por-produto / regra-de-produto: soma count dos SKUs (legado). Sem details → 105.
    if (rc?.tipo === 'regra-por-produto' || rc?.tipo === 'regra-de-produto') {
      const catalog = new Set((db.products[idPassbook] ?? []).map((pr) => String(pr.uniqueId)));
      let award = 0;
      for (const row of details) {
        const sku = String((row as { sku?: string }).sku ?? '');
        const count = Number((row as { count?: number }).count) || 0;
        if (sku && count > 0 && (catalog.size === 0 || catalog.has(sku))) {
          award += count;
        }
      }
      if (award <= 0) {
        return { status: 422, data: { status: '105', error: 'Venda ignorada.' } };
      }
      return {
        status: 201,
        data: {
          status: 'ok',
          mensagem: `Simulação: +${award} selos`,
          programa: 'selos',
          acao: a[2] === 'removePoints' ? 'resgate' : 'adição',
          valor: award,
        },
      };
    }

    const sim = previewOperatorBill({ passbook, purchaseValue: purchase });
    const programa =
      sim.program === 'cashback'
        ? 'cashback'
        : sim.program === 'points'
          ? 'pontos'
          : sim.program === 'giftcard' || sim.program === 'balance'
            ? 'giftcard'
            : 'selos';
    // Selos: mesma fórmula do engine (regra-de-valor, regra-fixa + regraRegua, etc.)
    const valor = sim.awardUnits;
    return {
      status: 201,
      data: {
        status: 'ok',
        mensagem: `Simulação: +${valor} ${programa}`,
        programa,
        acao: a[2] === 'removePoints' ? 'resgate' : 'adição',
        valor,
      },
    };
  }
  if (p === 'transaction/removePoints') {
    const forceFail = b.__forceAwardFail === true;
    if (forceFail) return { status: 422, data: { status: '101' } };
    const value = Number(b.value) || 0;
    if (value <= 0) return { status: 422, data: { status: '101' } };

    const idPassbook = resolveIdPassbook(b);
    const dedupErr = checkAndRegisterExternalId(idPassbook, b.externalId, { isRedemption: true });
    if (dedupErr) return dedupErr;

    const uniqueId = String(b.uniqueId ?? '');
    const lead = (db.leads[idPassbook] ?? []).find((l) =>
      uniqueIdsMatch(l.profile?.uniqueId, uniqueId),
    );
    const balance = lead?.total_points ?? 100;
    const remaining = Math.max(0, balance - value);
    if (lead) lead.total_points = remaining;

    return ok({ success: true, status: 'ok', log: { value: remaining } });
  }
  if (p === 'send' || p === 'transaction/addPoints' || p === 'resgate') {
    if (p === 'transaction/addPoints' && b.__forceFail === true) {
      return { status: 422, data: { status: '105' } };
    }

    const idPassbook = resolveIdPassbook(b);
    const isRedemption = p === 'resgate';
    const isAddPoints = p === 'transaction/addPoints' || p === 'send';
    const dedupErr = checkAndRegisterExternalId(idPassbook, b.externalId, {
      isRedemption,
      checkResgateNaoPontua: isAddPoints,
    });
    if (dedupErr) return dedupErr;

    // Persiste crédito no lead (sem isso a prévia “ganha X” some ao atualizar).
    if (isAddPoints) {
      const uniqueId = String(b.uniqueId ?? '');
      const award = Number(b.value) || Number(b.count_stamps) || 0;
      const lead = (db.leads[idPassbook] ?? []).find((l) =>
        uniqueIdsMatch(l.profile?.uniqueId, uniqueId),
      );
      if (lead && award > 0) {
        const prev = lead.total_points ?? 0;
        const next = Math.round((prev + award) * 100) / 100;
        lead.total_points = next;
        const log = {
          _id: `log_${Date.now().toString(36)}`,
          action: 'addPoints' as const,
          value: award,
          pointsChanged: award,
          date: new Date().toISOString(),
          externalId: b.externalId ? String(b.externalId) : undefined,
          loja: 'Operador',
        };
        lead.logs = [log, ...(lead.logs ?? [])];
      }
    }

    if (isRedemption) {
      const uniqueId = String(b.uniqueId ?? '');
      // /resgate (selos) — value pode vir em count; cash já tratado em removePoints
      const stamps = Number(b.count_stamps) || 0;
      const lead = (db.leads[idPassbook] ?? []).find((l) =>
        uniqueIdsMatch(l.profile?.uniqueId, uniqueId),
      );
      if (lead && stamps > 0) {
        lead.total_points = Math.max(0, (lead.total_points ?? 0) - stamps);
      }
    }

    return ok({ success: true, status: 'ok' });
  }
  if (p === 'change_password') return ok({ success: true });

  return notFound();
}
